import { useState } from "react";
import { authPost, errorMessage } from "../lib/authApi.ts";

// The admin app's sign-in page: one button, OIDC against the company's Google
// Workspace and nothing else. See ADR-0039.

type OperatorSignInProps = {
  // Where to land after Google: the page the operator asked for, or the shell.
  next: string;
  error?: string | null;
};

/**
 * Starts the Google sign-in and sends the browser to the authorisation URL
 * the auth library answers with; the callback sets the operator's session and
 * lands on `next`. Shows the server's refusal directly, or the `?error=` the
 * callback appended. Rendered by the admin entry at `/sign-in`.
 */
export function OperatorSignIn({ next, error: initialError = null }: OperatorSignInProps): JSX.Element {
  const [error, setError] = useState<string | null>(initialError);
  const [busy, setBusy] = useState(false);

  async function start(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const r = await authPost<{ url?: string; redirect?: boolean }>("/sign-in/social", {
        provider: "google",
        callbackURL: next,
        errorCallbackURL: "/sign-in",
      });
      if (r?.url) window.location.assign(r.url);
      else setError("The server returned no sign-in address.");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-card">
      <h1>Platform</h1>
      <p className="lede">Sign in with your company Google account to administer the factory.</p>
      {error ? (
        <div className="notice bad" role="alert">
          <div>{error}</div>
        </div>
      ) : null}
      <div className="btn-row">
        <button type="button" className="btn primary lg block" onClick={() => void start()} disabled={busy}>
          {busy ? "Opening Google…" : "Sign in with Google"}
        </button>
      </div>
    </div>
  );
}
